import { forwardRef, type ButtonHTMLAttributes, useState } from "react";
import { cn } from "@/lib/utils";
import { useNotification } from "@/hooks/useNotification";
import { useTranslation } from "@/hooks/useTranslation";
import { LoadingButton } from "@/components/ui/loading-button";
import { signIn } from "@/lib/auth-client";
import { PATHS } from "@/config/paths";

export interface GoogleSignInButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  isRegister?: boolean;
  className?: string;
}

export const GoogleAuthButton = forwardRef<HTMLButtonElement, GoogleSignInButtonProps>(
  ({ className = "", isRegister, ...props }, ref) => {
    const { showNotification } = useNotification();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    return (
      <LoadingButton
        loading={isLoading}
        data-umami-event="google-auth-button"
        spinnerClassName="absolute left-3"
        className={cn(
          "relative flex w-full items-center rounded-lg border px-3 py-2.5 text-md transition-all",
          "border-gray-300 bg-white text-gray-900 hover:bg-gray-50 active:bg-muted",
          "dark:border-transparent dark:bg-black dark:text-white dark:hover:bg-white/10 dark:active:bg-muted",
          className,
        )}
        ref={ref}
        {...props}
        onClick={async () => {
          try {
            setIsLoading(true);
            await signIn.social({
              provider: "google",
              errorCallbackURL: PATHS.AUTH_ERROR,
              callbackURL: PATHS.DASHBOARD,
              newUserCallbackURL: PATHS.NEW_USER,
            });
          } catch (error) {
            showNotification({
              message: t("auth.buttons.google_signin_failed"),
              type: "error",
            });
          } finally {
            setIsLoading(false);
          }
        }}
        type="button"
      >
        {!isLoading && (
          <svg
            className="absolute left-3 size-5"
            aria-hidden="true"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
        )}
        <span className="flex-1 text-center">
          {isRegister
            ? t("auth.buttons.continue_with_google")
            : t("auth.buttons.login_with_google")}
        </span>
      </LoadingButton>
    );
  },
);
